import { STANDARD_BLAETTERN, type BlaetterSlot, type Listenbereich, type SlotFelder } from "@gui-creator/gemeinsam";
import { Trash2, Ungroup } from "lucide-react";
import { useId, useState } from "react";
import { BlattZu } from "../huelle/Blatt.tsx";
import { FelderEditor, type FeldAenderung } from "./FelderEditor.tsx";
import { Abschnitt, eintragPlatzhalter, menuePlatzhalter, mische, quelleVon, ZustandReiter, type Kontext } from "./gemeinsam.tsx";
import { ItemWahl } from "./ItemWahl.tsx";

type Art = "zurueck" | "weiter" | "info";
const ARTEN: { art: Art; text: string }[] = [
  { art: "zurueck", text: "Zurück" },
  { art: "info", text: "Seitenanzeige" },
  { art: "weiter", text: "Weiter" },
];

/** Listenbereich mit Vorlage, Eintrags-Zuständen, Blättern und Leer-Inhalt; ein gewählter Blätter-Slot zeigt seine Darstellung. */
export function ListenFelder({ k, bereich, blaetterSlot }: { k: Kontext; bereich: Listenbereich; blaetterSlot?: Art }) {
  const id = useId();
  const [zustand, setZustand] = useState<string | null>(null);
  const quelle = quelleVon(k.katalog, bereich.quelle);
  const zustaende = quelle?.eintragZustaende ?? [];
  const aktiv = zustand && zustaende.includes(zustand) ? zustand : null;
  const setzeBereich = (neu: Listenbereich) => k.aendern([{ art: "listenbereich_setzen", bereich: neu }]);

  if (blaetterSlot) {
    const b = bereich.blaettern?.[blaetterSlot] as BlaetterSlot;
    const { slot, ...eigen } = b;
    const standard = STANDARD_BLAETTERN[blaetterSlot];
    return (
      <div className="panel-inhalt">
        <header className="panel-kopf">
          <h2>{ARTEN.find((a) => a.art === blaetterSlot)!.text}</h2>
          <span className="leise">
            Slot {slot} · Liste „{bereich.name}“
          </span>
          <BlattZu />
        </header>
        <p className="panel-hinweis">Erscheint nur, wenn es eine Seite davor oder danach gibt. Leere Felder nutzen die Standarddarstellung.</p>
        <FelderEditor
          k={k}
          felder={eigen}
          basis={standard}
          mitAktion={false}
          platzhalter={menuePlatzhalter(k)}
          setzen={(a) => setzeBereich({ ...bereich, blaettern: { ...bereich.blaettern, [blaetterSlot]: { ...mische(eigen, a), slot } } })}
        />
        {k.darf && (
          <div className="panel-fuss">
            <button
              type="button"
              className="knopf knopf-gefahr"
              onClick={() => {
                const rest = { ...bereich.blaettern };
                delete rest[blaetterSlot];
                setzeBereich({ ...bereich, blaettern: rest });
              }}
            >
              <Trash2 size={14} aria-hidden="true" /> Nicht mehr blättern
            </button>
          </div>
        )}
      </div>
    );
  }

  const abweichung: Partial<SlotFelder> = (aktiv && bereich.zustaende?.[aktiv]) || {};
  const setzeVorlage = (a: FeldAenderung) => {
    if (!aktiv) return setzeBereich({ ...bereich, vorlage: mische(bereich.vorlage, a) });
    setzeBereich({ ...bereich, zustaende: { ...(bereich.zustaende ?? {}), [aktiv]: mische(abweichung, a) } });
  };

  return (
    <div className="panel-inhalt">
      <header className="panel-kopf">
        <h2>Listenbereich „{bereich.name}“</h2>
        {k.darf && (
          <button type="button" className="knopf knopf-still" onClick={() => k.aendern([{ art: "listenbereich_entfernen", name: bereich.name }])}>
            <Ungroup size={14} aria-hidden="true" /> Auflösen
          </button>
        )}
        <BlattZu />
      </header>
      <Abschnitt titel="Daten">
        <div className="feld">
          <label htmlFor={`${id}-q`}>Datenquelle</label>
          <select id={`${id}-q`} className="eingabe" value={bereich.quelle} disabled={!k.darf} onChange={(e) => setzeBereich({ ...bereich, quelle: e.target.value })}>
            {!quelle && <option value={bereich.quelle}>{bereich.quelle} (unbekannt)</option>}
            {k.katalog.quellen.map((q) => (
              <option key={q.schluessel} value={q.schluessel}>
                {q.name}
              </option>
            ))}
          </select>
          <span className="feld-hinweis zahlen">
            {bereich.slots.length} Plätze · {quelle?.beispiel.eintraege.length ?? 0} Beispieleinträge
          </span>
        </div>
      </Abschnitt>
      <Abschnitt titel="Vorlage je Eintrag">
        <ZustandReiter zustaende={zustaende} aktiv={aktiv} onWahl={setZustand} belegt={(z) => !!bereich.zustaende?.[z]} />
        <FelderEditor k={k} felder={aktiv ? abweichung : bereich.vorlage} {...(aktiv ? { basis: bereich.vorlage } : {})} setzen={setzeVorlage} platzhalter={eintragPlatzhalter(k, bereich.quelle)} />
      </Abschnitt>
      <Abschnitt titel="Blättern">
        <ul className="blaetter-liste">
          {ARTEN.map(({ art, text }) => (
            <li key={art}>
              <span>{text}</span>
              <span className="zahlen leise">{bereich.blaettern?.[art] ? `Slot ${bereich.blaettern[art]!.slot}` : "nicht gesetzt"}</span>
            </li>
          ))}
        </ul>
        <p className="feld-hinweis">Einen freien Slot wählen und dort „Als Blätter-Slot“ nutzen.</p>
      </Abschnitt>
      <Abschnitt titel="Wenn die Liste leer ist" offen={false}>
        <ItemWahl label="Item" wert={bereich.leer?.symbol} leer="Nichts anzeigen" deaktiviert={!k.darf} onWert={(s) => setzeBereich({ ...bereich, leer: { ...(bereich.leer ?? {}), symbol: s } })} />
        {bereich.leer && k.darf && (
          <button
            type="button"
            className="knopf knopf-still"
            onClick={() => {
              const { leer: _, ...rest } = bereich;
              setzeBereich(rest);
            }}
          >
            Kein Hinweis bei leerer Liste
          </button>
        )}
      </Abschnitt>
    </div>
  );
}
